import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastHostService } from '../../shared/components/toast/toast-host.service';

export enum AlertType {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success'
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private readonly toastHost = inject(ToastHostService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  showInfo(message: string, duration: number = 5000): void {
    this.show(message, AlertType.INFO, duration);
  }

  showWarning(message: string, duration: number = 5000): void {
    this.show(message, AlertType.WARNING, duration);
  }

  showError(message: string, details?: string, duration: number = 8000): void {
    let displayMessage = message;
    
    // Handle common error codes
    if (details) {
      const translatedError = this.translateError(details);
      if (translatedError) {
        displayMessage = translatedError;
      } else if (details.includes('Channel.Connect.Failed')) {
        displayMessage = this.translate.instant('ERRORS.ERROR_500');
      }
    }
    
    this.show(displayMessage, AlertType.ERROR, duration);
  }

  showSuccess(message: string, duration: number = 3000): void {
    this.show(message, AlertType.SUCCESS, duration);
  }

  // Alias methods for convenience
  success(message: string, duration: number = 3000): void {
    this.showSuccess(message, duration);
  }

  error(message: string, details?: string, duration: number = 8000): void {
    this.showError(message, details, duration);
  }

  info(message: string, duration: number = 5000): void {
    this.showInfo(message, duration);
  }

  warning(message: string, duration: number = 5000): void {
    this.showWarning(message, duration);
  }

  private show(message: string, type: AlertType, duration: number): void {
    this.toastHost.push({ message, type, duration });
  }

  private translateError(errorCode: string): string | null {
    const key = `ERRORS.${errorCode}`;
    const translation = this.translate.instant(key);
    return translation !== key ? translation : null;
  }

  async confirm(title: string, message: string): Promise<boolean> {
    // Import dynamically to avoid circular dependencies
    const { ConfirmDialogComponent } = await import('../../shared/components/confirm-dialog/confirm-dialog.component');

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      panelClass: 'confirm-dialog-panel',
      data: { title, message }
    });

    return dialogRef.afterClosed().toPromise() || false;
  }

  async prompt(
    title: string,
    options?: {
      message?: string;
      inputLabel?: string;
      inputPlaceholder?: string;
      initialValue?: string;
      confirmText?: string;
      cancelText?: string;
      minLength?: number;
      maxLength?: number;
    }
  ): Promise<string | null> {
    // Import dynamically to avoid circular dependencies
    const { PromptDialogComponent } = await import('../../shared/components/prompt-dialog/prompt-dialog.component');

    const dialogRef = this.dialog.open(PromptDialogComponent, {
      width: '400px',
      panelClass: 'prompt-dialog-panel',
      data: { title, ...options }
    });

    return dialogRef.afterClosed().toPromise() || null;
  }
}
